import Alert from '@mui/material/Alert'
import AppBar from '@mui/material/AppBar'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Skeleton from '@mui/material/Skeleton'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import CheckboxIconOutlined from '@mui/icons-material/CheckBoxOutlined'
import LanguageIcon from '@mui/icons-material/Language'
import SchoolIcon from '@mui/icons-material/School'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import TimelapseIcon from '@mui/icons-material/Timelapse'
import WorkIcon from '@mui/icons-material/Work'
import EducationCard from 'Components/General/EducationCard'
import EmployerHistoryCard from 'Components/General/EmployerHistoryCard'
import LanguageCard from 'Components/General/LanguageCard'
import ProfileCard from 'Components/General/ProfileCard'
import LeisureActivitiesCard from 'pages/Profile/ProfilePageCards/LeisureActivitiesCard'
import ReferencesCard from 'pages/Profile/ProfilePageCards/ReferencesCard'
import SkillsCard from 'Components/General/SkillsCard'
import LearningGoalsCard from './LearningGoalsCard'
import ToolbarButton from 'Components/reusable/Buttons/ToolbarButton'
import ProjectsCard from 'Components/General/ProjectsCard'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import CardContentText from 'Components/reusable/CaleoCustomComponents/CardContentText'
import Grid from '@mui/material/Grid2'
import { cvAPI } from 'api/CvAPI'
import { useLanguageData, useUser } from 'hooks'
import { orderBy } from 'lodash'
import React, { useEffect, useState, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  IPersonCertificate,
  IPersonCompetence,
  ICourse,
  ICv,
  IEducation,
  IEmployer,
  ILeisureActivity,
  ILanguageSkill,
  IPersonIndustry,
  IPersonRole,
  IPersonSkill,
  IProject,
  IReference,
} from 'types/cvsInterfaces'
import { IError } from 'types/error'
import { INetwork } from 'types/networkInterfaces'
import { IPerson } from 'types/userInterfaces'
import { chooseDBTranslation } from 'utils/translations'
import { ICvData } from 'types/profileInterfaces'
import { Data } from 'types/hocTypes'
import useProfileWarnings from './warnings/useProfileWarnings'
import ProfileWarningsSummary from './ProfileWarningsSummary'
import SectionItemWarnings from './SectionItemWarnings'

/**
 * ProfilePageCardsProps interface represents the props for the ProfilePageCards component.
 * It provides the necessary data for rendering the component.
 * @notExported
 */
interface IProfilePageCardsProps {
  // The selected CV object
  selectedCv: ICv
  // The person data object
  personData: IPerson | undefined
  // Callback function to set the CV updated date
  setCvUpdatedAt: (date: Date) => void
  // Flag indicating if the CV is currently loading
  cvLoading: boolean
  // State tracking which sections are currently loading
  sectionLoadingState: Record<string, boolean>
  // Callback function to set the CV loading state
  setCvLoading: (value: boolean) => void
  // Flag indicating if the user has sales access
  salesAccess: boolean
  // Flag indicating if the user can access learning goals via same-organization sales or company admin permissions
  learningGoalsAccess: boolean
  // The account networks array
  accountNetworks: INetwork[] | undefined
  // Callback function to set the update CV progress
  setUpdateCvProgress: (update: Date) => void
}

/**
 * ProfilePageCards component.
 *
 * @param selectedCv - The selected CV object.
 * @param personData - The person data object.
 * @param setCvUpdatedAt - Callback function to set the CV updated date.
 * @param cvLoading - Flag indicating if the CV is currently loading.
 * @param setCvLoading - Callback function to set the CV loading state.
 * @param salesAccess - Flag indicating if the user has sales access.
 * @param viewerNetworks - The viewer networks array.
 * @param accountNetworks - The account networks array.
 * @param setUpdateCvProgress - Callback function to set the update CV progress.
 * @returns The ProfilePageCards component.
 * @notExported
 */
const ProfilePageCards: React.FC<IProfilePageCardsProps> = ({
  selectedCv,
  personData,
  setCvUpdatedAt,
  cvLoading,
  sectionLoadingState,
  setCvLoading,
  salesAccess,
  learningGoalsAccess,
  accountNetworks,
  setUpdateCvProgress,
}) => {
  const { user, groups, features } = useUser()
  const { t, i18n } = useTranslation()
  const [backendError, setBackendError] = useState<IError>()
  // Section items as edited on this page. `undefined` = not copied from `selectedCv` yet; an empty array means
  // the user removed every item, so it must not fall back to `selectedCv` (that brought deleted items back).
  const [employers, setEmployers] = useState<IEmployer[]>()
  const [projects, setProjects] = useState<IProject[]>()
  const [education, setEducation] = useState<IEducation[]>()
  const [courses, setCourses] = useState<ICourse[]>()
  const [certificates, setCertificates] = useState<IPersonCertificate[]>()
  const [competences, setCompetences] = useState<IPersonCompetence[]>()
  const [references, setReferences] = useState<IReference[]>()
  const [leisureActivities, setLeisureActivities] = useState<ILeisureActivity[]>()
  const [languageSkills, setLanguageSkills] = useState<ILanguageSkill[]>()
  const [skills, setSkills] = useState<IPersonSkill[]>()
  const [industries, setIndustries] = useState<IPersonIndustry[]>()
  const [roles, setRoles] = useState<IPersonRole[]>()
  const [overviewData, setOverviewData] = useState<ICvData>({
    Employers: [],
    Projects: [],
    Education: [],
    Courses: [],
    Certificates: [],
    Competences: [],
    LanguageSkills: [],
  })

  const profileRef = useRef<HTMLDivElement>(null)
  const profileSectionsToolbarRef = useRef<HTMLDivElement>(null)
  const skillsRef = useRef<HTMLDivElement>(null)
  const industriesRef = useRef<HTMLDivElement>(null)
  const rolesRef = useRef<HTMLDivElement>(null)
  const learningGoalsRef = useRef<HTMLDivElement>(null)
  const educationRef = useRef<HTMLDivElement>(null)
  const employersRef = useRef<HTMLDivElement>(null)
  const projectsRef = useRef<HTMLDivElement>(null)
  const referencesRef = useRef<HTMLDivElement>(null)
  const leisureActivitiesRef = useRef<HTMLDivElement>(null)
  const languagesRef = useRef<HTMLDivElement>(null)
  const { languages } = useLanguageData()
  const theme = useTheme()
  const mobileView = useMediaQuery(theme.breakpoints.down('md'))
  const renderSectionSkeleton = (height = 280) => (
    <Skeleton variant="rectangular" height={height} sx={{ borderRadius: 1 }} />
  )

  const warningsDataLoaded = useMemo(() => {
    const values = Object.values(sectionLoadingState ?? {})
    return values.length > 0 && values.every(isLoading => !isLoading)
  }, [sectionLoadingState])

  useEffect(() => {
    if (selectedCv) {
      setSkills(selectedCv.PersonSkills)
      setIndustries(selectedCv.Industries)
      setRoles(selectedCv.Roles)
      setEmployers(selectedCv.Employers)
      setProjects(selectedCv.Projects)
      setEducation(selectedCv.Education)
      setCourses(selectedCv.Courses)
      setCertificates(selectedCv.PersonCertificates)
      setCompetences(selectedCv.PersonCompetences)
      setReferences(selectedCv.References)
      setLeisureActivities(selectedCv.LeisureActivities ?? [])
      setLanguageSkills(selectedCv.LanguageSkills)
    }
  }, [selectedCv])

  const sectionData = useMemo(
    () => ({
      skills: skills ?? selectedCv.PersonSkills,
      industries: industries ?? selectedCv.Industries,
      roles: roles ?? selectedCv.Roles,
      employers: employers ?? selectedCv.Employers,
      projects: projects ?? selectedCv.Projects,
      education: education ?? selectedCv.Education,
      courses: courses ?? selectedCv.Courses,
      certificates: certificates ?? selectedCv.PersonCertificates,
      competences: competences ?? selectedCv.PersonCompetences,
      references: references ?? selectedCv.References,
      leisureActivities: leisureActivities ?? selectedCv.LeisureActivities ?? [],
      languageSkills: languageSkills ?? selectedCv.LanguageSkills,
    }),
    [
      certificates,
      competences,
      courses,
      education,
      employers,
      industries,
      languageSkills,
      projects,
      references,
      leisureActivities,
      roles,
      selectedCv.Courses,
      selectedCv.Education,
      selectedCv.Employers,
      selectedCv.Industries,
      selectedCv.LanguageSkills,
      selectedCv.PersonCertificates,
      selectedCv.PersonCompetences,
      selectedCv.PersonSkills,
      selectedCv.Projects,
      selectedCv.References,
      selectedCv.LeisureActivities,
      selectedCv.Roles,
      skills,
    ]
  )

  const profileWarnings = useProfileWarnings({
    personData,
    ...sectionData,
    activeLanguageCode: i18n.language,
    enabled: warningsDataLoaded,
  })

  useEffect(() => {
    setOverviewData((o: ICvData) => ({
      ...o,
      Employers: sectionData.employers,
      Projects: sectionData.projects,
      Education: sectionData.education,
      Courses: sectionData.courses,
      Certificates: sectionData.certificates,
      Competences: sectionData.competences,
      LanguageSkills: sectionData.languageSkills,
    }))

    setCvLoading(false)
  }, [sectionData, setCvLoading])

  const editable = useMemo(() => {
    if (selectedCv && user) {
      if (
        (groups && groups.includes('admin')) ||
        (!selectedCv.original && salesAccess) ||
        (user.Person && selectedCv.personId && user.Person.id === selectedCv.personId)
      ) {
        return true
      }
    }
    return false
  }, [salesAccess, selectedCv, user, groups])

  const showWarnings = useMemo(() => {
    const isOwner = !!(user?.Person && selectedCv?.personId && user.Person.id === selectedCv.personId)

    return learningGoalsAccess || isOwner
  }, [learningGoalsAccess, selectedCv?.personId, user?.Person])

  const warningsVisible = showWarnings && warningsDataLoaded

  const renderToolbarLabel = (label: React.ReactNode, warningCount: number) => {
    if (!warningsVisible) {
      return label
    }

    return (
      <Badge
        badgeContent={warningCount}
        color="warning"
        sx={{
          color: '#212121',
          '& .MuiBadge-badge': {
            color: '#212121',
          },
        }}
      >
        <Box component="span">{label}</Box>
      </Badge>
    )
  }

  const setUpdatedAt = async () => {
    if (selectedCv) {
      const updatedAt = await cvAPI.getUpdatedAt(selectedCv.id)
      setCvUpdatedAt(new Date(updatedAt))
    }
  }

  const updateOverviewData = (type: string, data: unknown[]) => {
    switch (type) {
      case 'employers': {
        setOverviewData({ ...overviewData, Employers: data as IEmployer[] })
        break
      }

      case 'projects': {
        setOverviewData({ ...overviewData, Projects: data as IProject[] })
        break
      }

      case 'education': {
        const updated = data as unknown as Data
        setOverviewData({ ...overviewData, Education: updated.education })
        break
      }

      case 'courses': {
        const updated = data as unknown as Data
        setOverviewData({ ...overviewData, Courses: updated.courses })
        break
      }

      case 'certificates': {
        const updated = data as unknown as Data
        setOverviewData({ ...overviewData, Certificates: updated.certificates })
        break
      }

      case 'competences': {
        const updated = data as unknown as Data
        setOverviewData({ ...overviewData, Competences: updated.competences })
        break
      }

      case 'languages': {
        setOverviewData({ ...overviewData, LanguageSkills: data as ILanguageSkill[] })
        break
      }

      default:
        break
    }
  }

  const updateProjects = (newProject: IProject) => {
    setProjects([...sectionData.projects, newProject])
  }

  const updateEmployers = (newEmployer: IEmployer) => {
    setEmployers([...sectionData.employers, newEmployer])
  }

  interface CVOption {
    label: string
    data: ICv | undefined
  }
  let CVOptions: CVOption[] = []

  if (personData && personData.CVs) {
    for (const cv of personData.CVs) {
      CVOptions.push({
        label: chooseDBTranslation(i18n, cv).name ? chooseDBTranslation(i18n, cv).name : String(cv.id),
        data: cv,
      })
    }

    CVOptions = orderBy(CVOptions, ['data.id'], ['asc'])
  }

  const scrollToRef = (ref: React.RefObject<HTMLDivElement | null>) => {
    if (ref && ref.current) {
      const toolbarHeight = profileSectionsToolbarRef.current?.getBoundingClientRect().height ?? 0
      const targetTop = window.scrollY + ref.current.getBoundingClientRect().top - toolbarHeight - 16

      window.scrollTo({
        top: Math.max(targetTop, 0),
        behavior: 'smooth',
      })
    }
  }

  if (cvLoading || !languages) {
    return null
  }

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  if (!personData && !cvLoading) {
    return (
      <Box p={{ md: 3, sm: 1, xs: 0 }} mt={{ xs: 1 }}>
        <CardContentText>{t('profile.noCv')}</CardContentText>
      </Box>
    )
  }

  return (
    <Grid
      container
      direction="row"
      justifyContent="center"
      alignItems="flex-start"
      sx={{ px: 1 }}
      data-profile-warnings-total={warningsVisible ? profileWarnings.totalMissingFields : undefined}
      data-profile-warnings-sections={warningsVisible ? profileWarnings.sectionsWithWarnings : undefined}
    >
      <Grid sx={{ maxWidth: '1400px !important' }} container spacing={3} mt={0} size={12}>
        {mobileView && warningsVisible && <ProfileWarningsSummary warnings={profileWarnings} />}
        {!mobileView && (
          <AppBar
            position="sticky"
            sx={{ borderRadius: '0px 0px 10px 10px' }}
            elevation={1}
            data-testid="profile-page-cards-appbar"
          >
            <Toolbar ref={profileSectionsToolbarRef} sx={{ borderRadius: '0px 0px 10px 10px' }}>
              <ToolbarButton clickAction={() => scrollToRef(profileRef)}>
                {renderToolbarLabel(t('profile'), profileWarnings.sections.profile.missingFields)}
              </ToolbarButton>
              <Divider orientation="vertical" />
              <ToolbarButton clickAction={() => scrollToRef(skillsRef)}>
                {renderToolbarLabel(t('skills.title'), profileWarnings.sections.skills.missingFields)}
              </ToolbarButton>
              <Divider orientation="vertical" />
              <ToolbarButton clickAction={() => scrollToRef(industriesRef)}>
                {renderToolbarLabel(t('industries.title'), profileWarnings.sections.industries.missingFields)}
              </ToolbarButton>
              <Divider orientation="vertical" />
              <ToolbarButton clickAction={() => scrollToRef(rolesRef)}>
                {renderToolbarLabel(t('roles.title'), profileWarnings.sections.roles.missingFields)}
              </ToolbarButton>
              <Divider orientation="vertical" />
              <ToolbarButton clickAction={() => scrollToRef(employersRef)}>
                {renderToolbarLabel(t('employer.title'), profileWarnings.sections.employers.missingFields)}
              </ToolbarButton>
              <Divider orientation="vertical" />
              <ToolbarButton clickAction={() => scrollToRef(projectsRef)}>
                {renderToolbarLabel(t('project.title'), profileWarnings.sections.projects.missingFields)}
              </ToolbarButton>
              <Divider orientation="vertical" />
              <ToolbarButton clickAction={() => scrollToRef(educationRef)}>
                {renderToolbarLabel(
                  t('education.title'),
                  profileWarnings.sections.education.missingFields +
                    profileWarnings.sections.courses.missingFields +
                    profileWarnings.sections.certificates.missingFields +
                    profileWarnings.sections.competences.missingFields
                )}
              </ToolbarButton>
              <Divider orientation="vertical" />
              <ToolbarButton clickAction={() => scrollToRef(referencesRef)}>
                {renderToolbarLabel(t('reference.title'), profileWarnings.sections.references.missingFields)}
              </ToolbarButton>
              <Divider orientation="vertical" />
              <ToolbarButton clickAction={() => scrollToRef(leisureActivitiesRef)}>
                {t('leisureActivity.title')}
              </ToolbarButton>
              <Divider orientation="vertical" />
              <ToolbarButton clickAction={() => scrollToRef(languagesRef)}>
                {renderToolbarLabel(t('language.title'), profileWarnings.sections.languages.missingFields)}
              </ToolbarButton>
              {features.includes('learningGoals') && learningGoalsAccess && <Divider orientation="vertical" />}
              {features.includes('learningGoals') && learningGoalsAccess && (
                <ToolbarButton clickAction={() => scrollToRef(learningGoalsRef)}>
                  {t('profile.learningGoals.title')}
                </ToolbarButton>
              )}
            </Toolbar>
          </AppBar>
        )}
        {!mobileView && (
          <Grid size={12}>
            <Alert severity="info">
              <Typography>{t('profile.translationGuide')}</Typography>
            </Alert>
          </Grid>
        )}
        {!mobileView && warningsVisible && <ProfileWarningsSummary warnings={profileWarnings} />}
        <Grid ref={profileRef} size={12}>
          {warningsVisible && (
            <SectionItemWarnings section="profile" sectionTitleKey="profile" warnings={profileWarnings} />
          )}
          <ProfileCard
            editable={editable}
            cv={selectedCv}
            setUpdatedAt={setUpdatedAt}
            data={overviewData}
            languages={languages}
            accountNetworks={accountNetworks}
            updateProgress={() => setUpdateCvProgress(new Date())}
          />
        </Grid>
        <Grid ref={skillsRef} size={12}>
          {warningsVisible && (
            <SectionItemWarnings section="skills" sectionTitleKey="skills.title" warnings={profileWarnings} />
          )}
          {sectionLoadingState?.person_skills ? (
            renderSectionSkeleton()
          ) : (
            <SkillsCard
              type="skill"
              details={true}
              update={true}
              editable={editable}
              cv={selectedCv}
              setUpdatedAt={setUpdatedAt}
              header="skills"
              avatar={<CheckboxIconOutlined />}
              items={sectionData.skills}
              setItems={setSkills}
              setProjects={setProjects}
              setEmployers={setEmployers}
              hideAddButton={!mobileView}
              updateProgress={() => setUpdateCvProgress(new Date())}
            />
          )}
        </Grid>
        <Grid ref={industriesRef} size={12}>
          {warningsVisible && (
            <SectionItemWarnings section="industries" sectionTitleKey="industries.title" warnings={profileWarnings} />
          )}
          {sectionLoadingState?.industries ? (
            renderSectionSkeleton()
          ) : (
            <SkillsCard
              type="industry"
              details={true}
              update={true}
              editable={editable}
              cv={selectedCv}
              setUpdatedAt={setUpdatedAt}
              header="industries"
              avatar={<CheckboxIconOutlined />}
              items={sectionData.industries}
              setItems={setIndustries}
              setProjects={setProjects}
              setEmployers={setEmployers}
              hideAddButton={!mobileView}
              updateProgress={() => setUpdateCvProgress(new Date())}
            />
          )}
        </Grid>
        <Grid ref={rolesRef} size={12}>
          {warningsVisible && (
            <SectionItemWarnings section="roles" sectionTitleKey="roles.title" warnings={profileWarnings} />
          )}
          {sectionLoadingState?.roles ? (
            renderSectionSkeleton()
          ) : (
            <SkillsCard
              type="role"
              details={true}
              update={true}
              editable={editable}
              cv={selectedCv}
              setUpdatedAt={setUpdatedAt}
              header="roles"
              avatar={<CheckboxIconOutlined />}
              items={sectionData.roles}
              setItems={setRoles}
              setProjects={setProjects}
              setEmployers={setEmployers}
              hideAddButton={!mobileView}
              updateProgress={() => setUpdateCvProgress(new Date())}
            />
          )}
        </Grid>
        <Grid ref={employersRef} size={12}>
          {warningsVisible && (
            <SectionItemWarnings section="employers" sectionTitleKey="employer.title" warnings={profileWarnings} />
          )}
          {sectionLoadingState?.employers ? (
            renderSectionSkeleton(300)
          ) : (
            <EmployerHistoryCard
              editable={editable}
              cv={selectedCv}
              setUpdatedAt={setUpdatedAt}
              header={'employer'}
              avatar={<WorkIcon />}
              items={sectionData.employers}
              setRoles={setRoles}
              updateOverviewData={updateOverviewData}
              updateProjects={updateProjects}
              updateProgress={() => setUpdateCvProgress(new Date())}
              setEmployers={setEmployers}
            />
          )}
        </Grid>
        <Grid ref={projectsRef} size={12}>
          {warningsVisible && (
            <SectionItemWarnings section="projects" sectionTitleKey="project.title" warnings={profileWarnings} />
          )}
          {sectionLoadingState?.projects ? (
            renderSectionSkeleton(320)
          ) : (
            <ProjectsCard
              editable={editable}
              cv={selectedCv}
              setUpdatedAt={setUpdatedAt}
              header={'project'}
              avatar={<TimelapseIcon />}
              items={sectionData.projects}
              setSkills={setSkills}
              setIndustries={setIndustries}
              setRoles={setRoles}
              updateOverviewData={updateOverviewData}
              updateProgress={() => setUpdateCvProgress(new Date())}
              updateEmployers={updateEmployers}
              setProjects={setProjects}
            />
          )}
        </Grid>
        <Grid ref={educationRef} size={12}>
          {warningsVisible && (
            <SectionItemWarnings section="education" sectionTitleKey="education.title" warnings={profileWarnings} />
          )}
          {warningsVisible && (
            <SectionItemWarnings
              section="courses"
              sectionTitleKey="profile.warnings.sectionLabel.courses"
              warnings={profileWarnings}
            />
          )}
          {warningsVisible && (
            <SectionItemWarnings
              section="certificates"
              sectionTitleKey="profile.warnings.sectionLabel.certificates"
              warnings={profileWarnings}
            />
          )}
          {warningsVisible && (
            <SectionItemWarnings
              section="competences"
              sectionTitleKey="profile.warnings.sectionLabel.competences"
              warnings={profileWarnings}
            />
          )}
          {sectionLoadingState?.education ||
          sectionLoadingState?.courses ||
          sectionLoadingState?.certificates ||
          sectionLoadingState?.competences ? (
            renderSectionSkeleton(360)
          ) : (
            <EducationCard
              editable={editable}
              cv={selectedCv}
              setUpdatedAt={setUpdatedAt}
              header={'education'}
              avatar={<SchoolIcon />}
              data={{
                education: sectionData.education,
                courses: sectionData.courses,
                certificates: sectionData.certificates,
                competences: sectionData.competences,
              }}
              updateOverviewData={updateOverviewData}
              updateProgress={() => setUpdateCvProgress(new Date())}
              setEducation={setEducation}
              setCourses={setCourses}
              setCertificates={setCertificates}
              setCompetences={setCompetences}
            />
          )}
        </Grid>
        <Grid ref={referencesRef} size={12}>
          {warningsVisible && (
            <SectionItemWarnings section="references" sectionTitleKey="reference.title" warnings={profileWarnings} />
          )}
          {sectionLoadingState?.references ? (
            renderSectionSkeleton(220)
          ) : (
            <ReferencesCard
              editable={editable}
              cv={selectedCv}
              setUpdatedAt={setUpdatedAt}
              header={'reference'}
              avatar={<ThumbUpIcon />}
              items={sectionData.references}
              updateProgress={() => setUpdateCvProgress(new Date())}
              setReferences={setReferences}
            />
          )}
        </Grid>
        <Grid ref={leisureActivitiesRef} size={12}>
          {sectionLoadingState?.leisure_activities ? (
            renderSectionSkeleton(220)
          ) : (
            <LeisureActivitiesCard
              editable={editable}
              cv={selectedCv}
              setUpdatedAt={setUpdatedAt}
              header={'leisureActivity'}
              avatar={<ThumbUpIcon />}
              items={sectionData.leisureActivities}
              updateProgress={() => setUpdateCvProgress(new Date())}
              setLeisureActivities={setLeisureActivities}
            />
          )}
        </Grid>
        <Grid ref={languagesRef} size={12}>
          {warningsVisible && (
            <SectionItemWarnings
              section="languages"
              sectionTitleKey="language.title"
              warnings={profileWarnings}
              languages={languages}
            />
          )}
          {sectionLoadingState?.language_skills ? (
            renderSectionSkeleton(220)
          ) : (
            <LanguageCard
              editable={editable}
              cv={selectedCv}
              setUpdatedAt={setUpdatedAt}
              header={'language'}
              avatar={<LanguageIcon />}
              items={sectionData.languageSkills}
              languages={languages}
              updateOverviewData={updateOverviewData}
              updateProgress={() => setUpdateCvProgress(new Date())}
              setLanguageSkills={setLanguageSkills}
            />
          )}
        </Grid>
        {features.includes('learningGoals') && learningGoalsAccess && personData?.id && (
          <Grid ref={learningGoalsRef} size={12} sx={{ pb: 3 }}>
            <LearningGoalsCard personId={personData.id} editable={editable} />
          </Grid>
        )}
      </Grid>
    </Grid>
  )
}

export default ProfilePageCards
